import React, { useState, useMemo } from "react";
import { TbSearch, TbArrowLeft, TbSettings2, TbLock, TbCircleCheck } from "react-icons/tb";
import { Button, Alert, EmptyState } from "../../components";
import {
  CATEGORIES, INTEGRATIONS, STATUS_LABEL, isManageable, findIntegration, IntegrationMark,
} from "./integrations/catalog";
import GoHighLevelPanel from "./integrations/GoHighLevelPanel";
import HubSpotPanel from "./integrations/HubSpotPanel";
import ZohoPanel from "./integrations/ZohoPanel";
import ZomentumPanel from "./integrations/ZomentumPanel";
import AiProviderPanel from "./integrations/AiProviderPanel";
import AteraPanel from "./integrations/AteraPanel";
import NinjaOnePanel from "./integrations/NinjaOnePanel";
import SyncroPanel from "./integrations/SyncroPanel";
import FreshservicePanel from "./integrations/FreshservicePanel";
import Microsoft365Panel from "./integrations/Microsoft365Panel";
import "./Settings.css";

// Integrations, as a directory rather than one connector's settings page.
//
// This used to be the GoHighLevel form with an empty state under it saying nothing else
// existed. That was honest but it hid the shape of the product: a reader could not see
// what Pipsqueak connects to, only what they happened to be configuring.
//
// So: a catalogue grouped by what the connector is FOR, and one card per connector whose
// status is a fact about the code (see integrations/catalog.js). Anything not built says
// "Planned" and has no button — a Connect that opens nothing is worse than an absence,
// because the reader spends their time finding out rather than reading it.
//
// The two real connectors keep their existing forms verbatim; they moved into
// integrations/ and are opened from their card.

const PANELS = {
  ghl: GoHighLevelPanel,
  hubspot: HubSpotPanel,
  zoho: ZohoPanel,
  zomentum: ZomentumPanel,
  ai: AiProviderPanel,
  atera: AteraPanel,
  ninjaone: NinjaOnePanel,
  syncro: SyncroPanel,
  freshservice: FreshservicePanel,
  m365: Microsoft365Panel,
};

const StatusChip = ({ status }) => (
  <span className={`v2-intg-status v2-intg-status--${status}`}>
    {status === "available" && <TbCircleCheck size={12} />}
    {status === "platform" && <TbLock size={12} />}
    {STATUS_LABEL[status]}
  </span>
);

const IntegrationCard = ({ item, onOpen }) => {
  const manageable = isManageable(item);
  return (
    <div className={`v2-intg-card${manageable ? "" : " v2-intg-card--quiet"}`}>
      <div className="v2-intg-card-head">
        <IntegrationMark item={item} />
        <div style={{ minWidth: 0 }}>
          <div className="v2-intg-card-name">{item.name}</div>
          <StatusChip status={item.status} />
        </div>
      </div>

      <p className="v2-intg-card-desc">{item.description}</p>
      {item.note && <p className="v2-intg-card-note">{item.note}</p>}

      {/* Only a connector that can actually be opened gets a control. The others end
          with their status, which has already said why. */}
      {manageable && (
        <Button className="v2-intg-card-btn" onClick={() => onOpen(item.id)}>
          <TbSettings2 size={15} /> Manage
        </Button>
      )}
    </div>
  );
};

const IntegrationsScreen = ({ isAdmin }) => {
  const [openId, setOpenId] = useState(null);
  const [search, setSearch] = useState("");

  const open = openId ? findIntegration(openId) : null;

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const match = (i) =>
      !q || i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q);
    return CATEGORIES
      .map((c) => ({ ...c, items: INTEGRATIONS.filter((i) => i.category === c.id && match(i)) }))
      .filter((c) => c.items.length > 0);
  }, [search]);

  // ── One connector's settings ───────────────────────────────────────────────
  if (open && isManageable(open)) {
    const Panel = PANELS[open.panel];
    return (
      <div className="v2-page">
        <div className="v2-pagehead">
          <div className="v2-intg-detail-head">
            <Button icon aria-label="Back to integrations" onClick={() => setOpenId(null)}>
              <TbArrowLeft size={16} />
            </Button>
            <IntegrationMark item={open} />
            <div>
              <h2>{open.name}</h2>
              <p>{open.description}</p>
            </div>
          </div>
        </div>

        {!isAdmin && <Alert tone="warning">Only administrators can change integration settings.</Alert>}
        <Panel isAdmin={isAdmin} />
      </div>
    );
  }

  // ── The catalogue ──────────────────────────────────────────────────────────
  return (
    <div className="v2-page">
      <div className="v2-pagehead">
        <div>
          <h2>Integrations</h2>
          <p>Connect Pipsqueak to the systems you already work in.</p>
        </div>
        <div className="v2-omni v2-intg-search">
          <TbSearch size={14} />
          <input type="search" value={search} placeholder="Search integrations"
                 aria-label="Search integrations"
                 onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {!isAdmin && <Alert tone="warning">Only administrators can change integration settings.</Alert>}

      {groups.length === 0 ? (
        <EmptyState title="No integrations match that search">
          Try a different name, or clear the search to see everything Pipsqueak connects to.
        </EmptyState>
      ) : (
        groups.map((c) => (
          <section key={c.id} className="v2-intg-group">
            <div className="v2-intg-group-head">
              <div className="v2-section-title">{c.name}</div>
              <p className="v2-intg-group-blurb">{c.blurb}</p>
            </div>
            <div className="v2-intg-grid">
              {c.items.map((i) => (
                <IntegrationCard key={i.id} item={i} onOpen={setOpenId} />
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
};

export default IntegrationsScreen;
